import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import type { Session } from "@supabase/supabase-js";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { supabaseV2 } from "../../lib/supabase";
import { track } from "../../lib/analytics";
import { AuthShell } from "../../components/shell/AuthShell";
import { FullScreenState } from "../../components/state";
import { PickYourselfStep } from "../../components/PickYourselfStep";

type InviteInfo = {
  team_id: string;
  team_name: string;
  team_slug: string;
  team_abbreviation: string | null;
  team_primary_color: string | null;
  team_secondary_color: string | null;
  team_logo_url: string | null;
  is_active: boolean;
};

export function JoinTeamPage({ token }: { token: string }) {
  // Pagina pubblica brandata: force light mode (stesso ragionamento di
  // PublicResponsePage, evita illeggibilita' in dark mode di sistema).
  const { t } = useTranslation("public");
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    supabaseV2.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });
    const { data } = supabaseV2.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const inviteQuery = useQuery({
    queryKey: ["team-invite-info", token],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("team_invite_info", { _token: token });
      if (error) throw error;
      const row = (data || [])[0];
      if (!row) throw new Error(t("join.errorNotFound"));
      return row as InviteInfo;
    },
    retry: false,
  });

  if (!authReady || inviteQuery.isLoading) {
    return <FullScreenState title={t("join.verifyingTitle")} description={t("join.verifying")} />;
  }

  if (inviteQuery.isError) {
    return (
      <AuthShell>
          <Card className="bg-transparent animate-fade-in">
            <CardHeader className="p-0 pb-5">
              <CardTitle className="text-[26px] font-bold leading-tight">{t("join.errorNotFound")}</CardTitle>
              <CardDescription>{t("join.errorNotFoundDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Button variant="default" onClick={() => (window.location.href = "/")}>
                {t("join.backHome")}
              </Button>
            </CardContent>
          </Card>
      </AuthShell>
    );
  }

  const info = inviteQuery.data!;
  return (
    <AuthShell>
        <Card className="bg-transparent animate-fade-in">
          <CardHeader className="p-0 pb-5 flex flex-row items-start gap-3">
            <div className="flex-1 space-y-1">
              <CardTitle className="text-[26px] font-bold leading-tight">{info.team_name}</CardTitle>
              <p className="text-sm text-muted-foreground">
                <Trans
                  i18nKey="join.description"
                  t={t}
                  values={{ teamName: info.team_name }}
                  components={{ strong: <strong /> }}
                />
              </p>
            </div>
          </CardHeader>
          <CardContent>
            {!info.is_active ? (
              <InviteDisabled />
            ) : session ? (
              <LoggedInJoin token={token} teamName={info.team_name} />
            ) : (
              <AnonymousSignupForm
                token={token}
                teamId={info.team_id}
                teamName={info.team_name}
                teamSlug={info.team_slug}
              />
            )}
          </CardContent>
        </Card>
    </AuthShell>
  );
}

function InviteDisabled() {
  const { t } = useTranslation("public");
  return (
    <div className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
      {t("join.disabled")}
    </div>
  );
}

function LoggedInJoin({ token, teamName }: { token: string; teamName: string }) {
  const { t } = useTranslation("public");
  const [joining, setJoining] = useState(true);
  const [consumeError, setConsumeError] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [autoTried, setAutoTried] = useState(false);
  const [selfFirstName, setSelfFirstName] = useState("");
  const [selfLastName, setSelfLastName] = useState("");
  const [alreadyLinked, setAlreadyLinked] = useState(false);

  // Auto-consume al primo mount + carica metadata utente
  useEffect(() => {
    if (autoTried) return;
    setAutoTried(true);
    (async () => {
      setJoining(true);
      setConsumeError(null);
      const { data: userData } = await supabaseV2.auth.getUser();
      const meta = (userData.user?.user_metadata || {}) as Record<string, string>;
      setSelfFirstName(meta.first_name || "");
      setSelfLastName(meta.last_name || "");

      const { data: consumedTeamId, error: rpcErr } = await supabaseV2.rpc("consume_team_invite", {
        _token: token,
      });
      if (rpcErr || !consumedTeamId) {
        setJoining(false);
        setConsumeError(rpcErr?.message || String(rpcErr || "errore"));
        return;
      }
      const newTeamId = consumedTeamId as string;
      setTeamId(newTeamId);

      // Se l'utente e' gia' linkato a un player del team, niente step "Sei tu?"
      if (userData.user) {
        const { data: linkedPlayer } = await supabaseV2
          .from("players")
          .select("id")
          .eq("team_id", newTeamId)
          .eq("user_id", userData.user.id)
          .maybeSingle();
        if (linkedPlayer) {
          setAlreadyLinked(true);
          window.setTimeout(() => {
            window.location.href = "/me";
          }, 800);
          return;
        }
      }
      setJoining(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (consumeError) {
    return (
      <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
        {consumeError}
      </div>
    );
  }

  if (alreadyLinked) {
    return (
      <div className="rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
        <Trans
          i18nKey="join.doneTitle"
          t={t}
          values={{ teamName }}
          components={{ strong: <strong /> }}
        />{" "}
        {t("join.doneRedirect")}
      </div>
    );
  }

  if (joining || !teamId) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("join.joining")}
      </div>
    );
  }

  return (
    <PickYourselfStep
      teamId={teamId}
      teamName={teamName}
      firstNameHint={selfFirstName}
      lastNameHint={selfLastName}
      onGoToApp={() => (window.location.href = "/me")}
    />
  );
}

type SignupMode = "signup" | "login";

function AnonymousSignupForm({
  token,
  teamId,
  teamName,
  teamSlug,
}: {
  token: string;
  teamId: string;
  teamName: string;
  teamSlug: string;
}) {
  const { t } = useTranslation("public");
  const { t: tAuth } = useTranslation("auth");
  const [mode, setMode] = useState<SignupMode>("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signupDone, setSignupDone] = useState(false);

  const continueUrl = useMemo(() => {
    const u = new URL(window.location.origin + "/join/" + encodeURIComponent(token));
    return u.toString();
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    if (mode === "signup") {
      const fName = firstName.trim();
      const lName = lastName.trim();
      if (!fName || !lName) {
        setLoading(false);
        setError(t("join.errorNameRequired"));
        return;
      }
      const { error: signUpError } = await supabaseV2.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: continueUrl,
          // team_* finiscono in raw_user_meta_data: l'edge function
          // notify-admin-signup li legge per includere la squadra scelta
          // nella mail di alert all'admin.
          data: {
            first_name: fName,
            last_name: lName,
            full_name: `${fName} ${lName}`.trim(),
            invite_token: token,
            team_id: teamId,
            team_name: teamName,
            team_slug: teamSlug,
          },
        },
      });
      setLoading(false);
      if (signUpError) {
        setError(signUpError.message);
        return;
      }
      // GA4 standard event "sign_up" — funnel di acquisizione via invito diretto.
      track.signUp({ source: "invite_link" });
      setSignupDone(true);
      return;
    }
    // login
    const { error: loginError } = await supabaseV2.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setLoading(false);
    if (loginError) {
      setError(loginError.message);
    }
  };

  // Schermata dedicata post-signup: stesso messaggio, ma niente form
  // ricompilabile ne' pulsante di reinvio.
  if (signupDone) {
    return (
      <div className="space-y-3 animate-fade-in">
        <div className="rounded-xl bg-sky-50 px-3 py-3 text-sm text-sky-800">
          <p className="font-semibold">{t("join.signupDoneTitle")}</p>
          <p className="text-xs mt-1">{t("join.signupAccountCreated")}</p>
        </div>
        <p className="text-xs text-muted-foreground text-center leading-relaxed">
          <Trans
            i18nKey="join.signupDoneEmailHint"
            t={t}
            values={{ email: email.trim() }}
            components={{ strong: <strong /> }}
          />
        </p>
      </div>
    );
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="inline-flex rounded-2xl bg-background p-0.5 text-sm w-full">
        <button
          type="button"
          onClick={() => setMode("signup")}
          className={
            "flex-1 px-3 py-1.5 rounded font-medium transition-colors " +
            (mode === "signup" ? "bg-card text-foreground " : "text-muted-foreground")
          }
        >
          {t("join.newTab")}
        </button>
        <button
          type="button"
          onClick={() => setMode("login")}
          className={
            "flex-1 px-3 py-1.5 rounded font-medium transition-colors " +
            (mode === "login" ? "bg-card text-foreground " : "text-muted-foreground")
          }
        >
          {t("join.existingTab")}
        </button>
      </div>

      {mode === "signup" && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="join-fn" className="text-xs">{tAuth("labels.firstName")}</Label>
            <Input id="join-fn" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoComplete="given-name" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="join-ln" className="text-xs">{tAuth("labels.lastName")}</Label>
            <Input id="join-ln" value={lastName} onChange={(e) => setLastName(e.target.value)} required autoComplete="family-name" />
          </div>
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor="join-email" className="text-xs">{tAuth("labels.email")}</Label>
        <Input id="join-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="join-pwd" className="text-xs">{tAuth("labels.password")}</Label>
        <Input
          id="join-pwd"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
        />
      </div>

      {error && (
        <div className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
      )}

      <Button variant="hero" type="submit" className="w-full" disabled={loading}>
        {loading ? (
          <>
            <Loader2 className="animate-spin" />
            {mode === "signup" ? t("join.creating") : t("join.loggingIn")}
          </>
        ) : mode === "signup" ? (
          <>
            <UserPlus />
            {t("join.createAndJoin", { teamName })}
          </>
        ) : (
          <>
            <LogIn />
            {t("join.loginAndJoin", { teamName })}
          </>
        )}
      </Button>
      <p className="text-xs text-muted-foreground text-center leading-relaxed">
        <Trans
          i18nKey="join.afterConfirm"
          t={t}
          values={{ teamName }}
          components={{ strong: <strong /> }}
        />
      </p>
    </form>
  );
}
